/* eslint-disable react/no-unescaped-entities */
import { ArrowRight } from 'lucide-react';
import { PageHero } from '../components/SectionHeading';
import { ScrollReveal } from '../components/Motion';
import { ASSETS, CAPABILITIES, pick } from '../data/content';
import { useLang } from '../i18n/LanguageContext';

const CHAIN_EN = [
    { label: 'Upstream', items: ['Exploration', 'Appraisal', 'Development', 'Production'] },
    { label: 'Midstream', items: ['Pipeline', 'Storage', 'LNG Terminal'] },
    { label: 'Downstream', items: ['Power Generation', 'Gas Sales', 'Refining', 'LNG Export'] },
];
const CHAIN_AR = [
    { label: 'القطاع العلوي', items: ['الاستكشاف', 'التقييم', 'التطوير', 'الإنتاج'] },
    { label: 'القطاع الأوسط', items: ['الأنابيب', 'التخزين', 'محطة الغاز المسال'] },
    { label: 'القطاع السفلي', items: ['توليد الطاقة', 'مبيعات الغاز', 'التكرير', 'تصدير الغاز المسال'] },
];

const CAP_TABLE_EN = [
    ['Upstream E&P', 'Central Asia · North Africa · Sub-Saharan Africa · Latin America · Middle East'],
    ['Midstream Gas Infrastructure', 'Central Asia (Afghanistan) · Latin America (Venezuela)'],
    ['Downstream Gas Monetization', 'Afghanistan · Venezuela · North Africa'],
    ['Financial Engineering', 'All geographies — multi-jurisdictional structuring'],
    ['Government Relations', 'Middle East · Central Asia · Africa · Latin America'],
    ['Cross-cultural Operations', 'Global — East-West integrated platform'],
];
const CAP_TABLE_AR = [
    ['الاستكشاف والإنتاج في القطاع العلوي', 'آسيا الوسطى · شمال إفريقيا · إفريقيا جنوب الصحراء · أمريكا اللاتينية · الشرق الأوسط'],
    ['البنية التحتية للغاز في القطاع الأوسط', 'آسيا الوسطى (أفغانستان) · أمريكا اللاتينية (فنزويلا)'],
    ['تحقيق قيمة الغاز في القطاع السفلي', 'أفغانستان · فنزويلا · شمال إفريقيا'],
    ['الهندسة المالية', 'جميع المناطق — هيكلة متعددة الولايات القضائية'],
    ['العلاقات الحكومية', 'الشرق الأوسط · آسيا الوسطى · إفريقيا · أمريكا اللاتينية'],
    ['العمليات بين الثقافات', 'عالمي — منصة متكاملة بين الشرق والغرب'],
];

const Capabilities = () => {
    const { t, lang } = useLang();
    const CHAIN = lang === 'ar' ? CHAIN_AR : CHAIN_EN;
    const CAP_TABLE = lang === 'ar' ? CAP_TABLE_AR : CAP_TABLE_EN;
    return (
        <div data-testid="capabilities-page">
            <PageHero
                eyebrow={t('capabilities.hero_eyebrow')}
                title={t('capabilities.hero_title')}
                sub={t('capabilities.hero_sub')}
                image={ASSETS.refinery}
            />

            {/* Value Chain Infographic */}
            <section className="bg-navy-deep py-24 md:py-32 grain-overlay">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <ScrollReveal>
                        <div className="text-center max-w-2xl mx-auto mb-16">
                            <div className="eyebrow mb-5">{t('capabilities.chain_eyebrow')}</div>
                            <h2 className="editorial-h2 text-white text-3xl md:text-4xl">
                                {t('capabilities.chain_h2')}
                            </h2>
                            <p className="mt-5 text-white/65 leading-relaxed">
                                {t('capabilities.chain_sub')}
                            </p>
                        </div>
                    </ScrollReveal>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {CHAIN.map((segment, idx) => (
                            <ScrollReveal key={segment.label} delay={idx * 0.1}>
                                <div className="border border-gold/30 p-8 h-full bg-navy/40 backdrop-blur">
                                    <div className="flex items-center justify-between mb-6">
                                        <div className="metric-number text-2xl md:text-3xl">0{idx + 1}</div>
                                        <ArrowRight className="w-5 h-5 text-gold opacity-50" />
                                    </div>
                                    <h3 className="font-display text-gold text-2xl mb-6">{segment.label}</h3>
                                    <ul className="space-y-3">
                                        {segment.items.map((it) => (
                                            <li key={it} className="text-white/80 text-sm flex items-center gap-3">
                                                <span className="w-1.5 h-1.5 bg-gold rounded-full" />
                                                {it}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </ScrollReveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Capability detail sections */}
            <section className="bg-cream py-12">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    {CAPABILITIES.map((c, i) => (
                        <ScrollReveal key={c.n}>
                            <div className={`py-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start ${i !== CAPABILITIES.length - 1 ? 'border-b border-navy/10' : ''}`}>
                                <div className="lg:col-span-4">
                                    <div className="metric-number text-4xl md:text-6xl mb-4">{c.n}</div>
                                    <div className="eyebrow text-navy mb-3">Capability {c.n}</div>
                                    <h3 className="editorial-h2 text-navy text-2xl md:text-3xl mb-2">
                                        {pick(c, 'title', lang)}
                                    </h3>
                                    <p className="text-ink/60 text-sm">{pick(c, 'sub', lang)}</p>
                                </div>
                                <div className="lg:col-span-8">
                                    <p className="text-ink/80 text-base md:text-lg leading-relaxed mb-8">
                                        {pick(c, 'body', lang)}
                                    </p>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 mb-10">
                                        {(lang === 'ar' ? c.points_ar : c.points).map((p, idx) => (
                                            <div key={idx} className="flex items-start gap-3 text-ink/80 text-sm">
                                                <span className="w-1 h-1 bg-gold rounded-full mt-2.5 flex-shrink-0" />
                                                <span>{p}</span>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="border-t border-gold/30 pt-6">
                                        <div className="eyebrow mb-3">{t('capabilities.track_label')}</div>
                                        <p className="text-ink/70 text-sm md:text-base leading-relaxed">{pick(c, 'track', lang)}</p>
                                    </div>
                                </div>
                            </div>
                        </ScrollReveal>
                    ))}
                </div>
            </section>

            {/* Summary Table */}
            <section className="bg-white py-24">
                <div className="max-w-6xl mx-auto px-6 lg:px-10">
                    <div className="eyebrow mb-5">{t('capabilities.summary_eyebrow')}</div>
                    <h2 className="editorial-h2 text-navy text-3xl md:text-4xl mb-12">
                        {t('capabilities.summary_h2')}
                    </h2>
                    <div className="overflow-x-auto">
                        <table className="editorial-table w-full" data-testid="capability-summary">
                            <thead>
                                <tr>
                                    <th>{t('capabilities.col_cap')}</th>
                                    <th>{t('capabilities.col_region')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {CAP_TABLE.map(([cap, regions]) => (
                                    <tr key={cap}>
                                        <td className="font-display text-navy">{cap}</td>
                                        <td className="text-ink/75">{regions}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Capabilities;
